import React, { useRef } from 'react';
import withStyles from '@mui/styles/withStyles';
import { withSnackbarMessage } from '../../context/SnackbarMessage';
import { compose } from '../../reactApolloCompat';
import { DialogContent } from '@mui/material';
import TextField from '../../component/form/TextField';
import AddressBookAutocomplete from '../../component/form/AddressBookAutocomplete';
import { ADDRESS_BOOK_CATEGORY } from '../funeral/funeralConstants';
import Typography from '../../component/Typography';
import Grid from '../../component/form/Grid';
import { joinDefined } from '../../util/strings';
import Label from '../../component/form/Label';
import Checkbox from '../../component/form/Checkbox';
import StaffAutoComplete from '../../component/form/StaffAutoComplete';
import OptionalTextField from '../../component/form/OptionalTextField';
import { dateToString, dateToTimeString } from '../../util/date';
import UserCircle from '../../component/form/UserCircle';
import { InlineHeader } from '../../component/form/Inline';
import DisplayContact from '../../component/bookings/DisplayContact';
import LinkButton from '../../component/form/LinkButton';

function NewTransferForm(props) {
    const { form, classes } = props;
    const allTransfers = form.getField('Transfers') || [];
    const onAssignStaff = (idx, user) => {
        const hasAlready = allTransfers[idx].AssignedStaff.findIndex(obj => obj.ID === user.ID);
        if (-1 === hasAlready) {
            allTransfers[idx].AssignedStaff.push(user);
            form.setField({ Transfers: allTransfers });
        }
    };
    const onUnassignStaff = (idx, user) => {
        const hasAlready = allTransfers[idx].AssignedStaff.findIndex(obj => obj.ID === user.ID);
        if (-1 !== hasAlready) {
            allTransfers[idx].AssignedStaff.splice(hasAlready, 1);
            form.setField({ Transfers: allTransfers });
        }
    };

    const handleResetDestination = () => {
        form.setField({ ['Transfers[' + idx + '].Destination']: null });
    };

    const deceasedName = joinDefined(
        [form.getField('FirstName'), form.getField('MiddleName'), form.getField('Surname')],
        ' '
    );

    const idx = allTransfers.length - 1;
    const transfer = allTransfers.slice(-1)[0];
    const lastCompleted = !!transfer.TransferActioned;
    const destinationContact = form.getField('Transfers[' + idx + '].Destination');
    const hasInitialDeparture = useRef(transfer.DepartureText);

    return (
        <>
            <DialogContent className={classes.diagBody}>
                <Grid container spacing={2}>
                    <Grid pc={1}>
                        <div style={{ paddingLeft: 16 }}>
                            <Typography variant="headline">
                                {form.getField('LegacyKey')}: {deceasedName}
                            </Typography>
                        </div>
                    </Grid>
                    <Grid sm={12} md={5}>
                        <Grid bucket>
                            <Grid item xs={12}>
                                <InlineHeader header="Collect from" />
                                {hasInitialDeparture.current ? (
                                    <div style={{ whiteSpace: 'pre-line' }}>
                                        {transfer.DepartureText}
                                        <br />
                                        <small>
                                            <a
                                                target={'_blank'}
                                                rel="noreferrer"
                                                href={
                                                    'https://www.google.com/maps/dir/?api=1&destination=' +
                                                    encodeURI(transfer.DepartureText.replace(/\n+/g, ': '))
                                                }
                                            >
                                                Get directions
                                            </a>
                                        </small>
                                    </div>
                                ) : (
                                    <TextField form={form} name={`Transfers[${idx}].DepartureText`} multiline />
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <InlineHeader header="Precautions" />
                                <Checkbox form={form} name={`Transfers[${idx}].Bariatric`} label="Bariatric" />
                                <Checkbox form={form} name={`Transfers[${idx}].InfectionRisk`} label="Infectious" />
                                <Checkbox form={form} name={`Transfers[${idx}].Decomposed`} label="Decomposed" />
                            </Grid>
                            <Grid item xs={12}>
                                <OptionalTextField
                                    form={form}
                                    name={`Transfers[${idx}].Notes`}
                                    label="Staff Notes"
                                    buttonlabel="+ Add Notes"
                                    multiline
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid sm={12} md={7}>
                        <Grid bucket>
                            <Grid item xs={12}>
                                <Label>Deliver to</Label>
                                {destinationContact?.ID > 0 ? (
                                    <>
                                        <span style={{ float: 'right', fontSize: '0.875rem' }}>
                                            <LinkButton onClick={handleResetDestination} text="Change destination" />
                                        </span>
                                        <DisplayContact contactID={destinationContact?.ID} />
                                    </>
                                ) : (
                                    <AddressBookAutocomplete
                                        name={'Transfers[' + idx + '].Destination'}
                                        placeholder={'Choose a mortuary...'}
                                        form={form}
                                        categories={[ADDRESS_BOOK_CATEGORY.mortuary]}
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <StaffAutoComplete
                                    label={'Assign Staff'}
                                    selectProps={{ multiple: true }}
                                    onSelect={(_, user) => onAssignStaff(idx, user)}
                                />
                                {transfer.AssignedStaff.length > 0 && (
                                    <div className={classes.memberIcons}>
                                        {transfer.AssignedStaff.map(obj => (
                                            <UserCircle
                                                key={obj.ID}
                                                user={joinDefined([obj.FirstName, obj.Surname], ' ')}
                                                onDelete={() => onUnassignStaff(idx, obj)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </Grid>
                        </Grid>
                        <Grid bucket>
                            <Grid pc={1}>
                                <Checkbox
                                    form={form}
                                    name={`Transfers[${idx}].TransferActioned`}
                                    label="Transfer Actioned?"
                                    onChange={(_, checked) =>
                                        form.setField({
                                            [`Transfers[${idx}].CompletedAt`]: !checked
                                                ? null
                                                : dateToString(new Date()),
                                            [`Transfers[${idx}].CompletedAtTime`]: !checked
                                                ? null
                                                : dateToTimeString(new Date())
                                        })
                                    }
                                />
                            </Grid>
                            {lastCompleted && (
                                <>
                                    <Grid pc={0.5}>
                                        <TextField
                                            required
                                            label="Date Actioned"
                                            name={`Transfers[${idx}].CompletedAt`}
                                            form={form}
                                            type="date"
                                            notFuture
                                        />
                                    </Grid>
                                    <Grid pc={0.5}>
                                        <TextField
                                            label="Time Actioned"
                                            name={`Transfers[${idx}].CompletedAtTime`}
                                            form={form}
                                            type="time"
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
        </>
    );
}

const styles = ({ breakpoints, spacing, palette, funeralHome }) => ({
    diagBody: {
        padding: spacing(1),
        backgroundColor: '#fafafa',
        '& > :first-child': {
            minHeight: 0,
            overflowY: 'auto',
            flex: '1 1 auto',
            padding: spacing(3),
            [breakpoints.down('sm')]: {
                padding: spacing(1)
            }
        }
    },
    memberIcons: {
        display: 'flex',
        flexWrap: 'wrap',
        rowGap: spacing(1),
        columnGap: spacing(1)
    }
});

export default compose(withSnackbarMessage, withStyles(styles))(NewTransferForm);
